import { Spin } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UNIT_MEASURE } from '@common/constants';
import { ProductType } from '@common/enums';
import { getEEProductType } from '@common/helpers';
import formatUOM from '@common/helpers/formatUOM';
import { OptionalProduct, SelectedItemDetailEntry, SelectedItemMap, SelectedItemProperties } from '@common/models';
import { getProductDescription } from '@common/utils';
import { formatPriceUnitOrFree } from '@common/utils/formatPriceUnitOrFree';
import { isHiddenProduct } from '@common/utils/productFilterUtility';
import { SectionSubtitle } from '@core/components';
import { useOptionalItems } from '@ee-api/optional-items-api/useOptionalItems';
import { useOptionalProductContext } from '@ee-context';

import OptionalProductItem from './components/OptionalProductItem';
import OptionalProductWithDetailsItem from './components/OptionalProductWithDetailsItem';
import { selectWarrantyInfoContent } from './components/helpers/selectWarrantyInfoContent';

import './OptionalProductsSection.scss';

const OptionalProductsSection = (): JSX.Element => {
    const { t } = useTranslation();
    const { isFetchingOptionalItems, optionalProducts } = useOptionalItems();
    const { selectedOptionalProducts, assignSelectedOptionalProducts, areOptionalProductsAvailable } =
        useOptionalProductContext();

    const toggleProductSelection = (optionalProduct: OptionalProduct) => {
        const selectedOptPrdDraft: SelectedItemMap = new Map();
        const currentSelection = selectedOptionalProducts.get(optionalProduct.productId);
        if (currentSelection) {
            selectedOptPrdDraft.set(optionalProduct.productId, {
                ...currentSelection,
                isSelected: !currentSelection.isSelected,
            });
        } else {
            selectedOptPrdDraft.set(optionalProduct.productId, {
                quantity: 0,
                isSelected: true,
                isManuallyChanged: false,
            });
        }
        assignSelectedOptionalProducts(selectedOptPrdDraft);
    };

    const onCustomWorkClick = (optionalProduct: OptionalProduct) => {
        const currentSelection = selectedOptionalProducts.get(optionalProduct.productId);
        if (currentSelection && currentSelection.isSelected && currentSelection.quantity > 0) {
            return;
        }
        toggleProductSelection(optionalProduct);
    };

    const toggleWarrantySelection = (optionalProduct: OptionalProduct) => {
        const selectedOptPrdDraft: SelectedItemMap = new Map();
        const currentSelection = selectedOptionalProducts.get(optionalProduct.productId);
        if (currentSelection) {
            // Is going to be selected
            if (!currentSelection.isSelected) {
                deselectMatchingWarranties(optionalProduct);
            }
            selectedOptPrdDraft.set(optionalProduct.productId, {
                ...currentSelection,
                isSelected: !currentSelection.isSelected,
            });
        } else {
            deselectMatchingWarranties(optionalProduct);
            selectedOptPrdDraft.set(optionalProduct.productId, {
                quantity: 1,
                isSelected: true,
                isManuallyChanged: false,
            });
        }
        assignSelectedOptionalProducts(selectedOptPrdDraft);
    };

    const deselectMatchingWarranties = (matchingProduct: OptionalProduct) => {
        const { qteSubCodeId } = matchingProduct;
        const selectedOptPrdDraft: SelectedItemMap = new Map();
        optionalProducts.forEach((optionalProduct) => {
            const optionalProductType = getEEProductType(optionalProduct);
            if (optionalProductType === ProductType.WARRANTY && qteSubCodeId === optionalProduct.qteSubCodeId) {
                const warrantySelection = selectedOptionalProducts.get(optionalProduct.productId);
                if (warrantySelection && warrantySelection.isSelected) {
                    selectedOptPrdDraft.set(optionalProduct.productId, {
                        ...warrantySelection,
                        isSelected: false,
                    });
                }
            }
        });
        assignSelectedOptionalProducts(selectedOptPrdDraft);
    };

    interface SelectedProductProps {
        quantity?: number;
        note?: string;
        detailEntries?: SelectedItemDetailEntry[];
    }

    const setSelectedProductProps = (optionalProduct: OptionalProduct, props: SelectedProductProps) => {
        const currentSelection = selectedOptionalProducts.get(optionalProduct.productId);
        if (currentSelection) {
            const selectedOptPrdDraft: SelectedItemMap = new Map();
            selectedOptPrdDraft.set(optionalProduct.productId, {
                ...currentSelection,
                ...props,
                isManuallyChanged: true,
            });
            assignSelectedOptionalProducts(selectedOptPrdDraft);
        }
    };

    const trimNote = (note: string) => {
        return note.replace(/\n+/g, '\n').trim();
    };

    const makeCondensedNote = (quantity: number, unitPrice: number, _unitOfMeasure: string, note: string): string => {
        const condensedNote = trimNote(note);
        const formattedPrice = formatPriceUnitOrFree(quantity * unitPrice);
        return `(${formattedPrice}): ${condensedNote}`;
    };

    const updateProductDetails = (product: OptionalProduct, newDetails: SelectedItemDetailEntry[]) => {
        const newMap: SelectedItemMap = new Map(selectedOptionalProducts);
        const totalQuantity = newDetails.reduce((previousValue, currentValue) => {
            return previousValue + currentValue.quantity;
        }, 0);
        const condensedNotes = newDetails
            .map((value) =>
                makeCondensedNote(value.quantity, product.price, formatUOM(product.unitOfMeasure), value.note)
            )
            .join('\n');
        const newItem: SelectedItemProperties = {
            quantity: totalQuantity,
            note: condensedNotes,
            detailEntries: newDetails.map((item) => {
                return { ...item, note: trimNote(item.note) };
            }),
            isManuallyChanged: true,
            isSelected: totalQuantity > 0,
        };
        newMap.set(product.productId, newItem);
        assignSelectedOptionalProducts(newMap);
    };

    /**
     * Sorts the optional products by description AND filters out any that should be hidden
     * @returns Sorted array of optional products
     */
    const sortedOptionalProducts = useMemo(() => {
        return optionalProducts
            .filter((product) => !isHiddenProduct(product))
            .toSorted((a, b) => getProductDescription(a).localeCompare(getProductDescription(b)));
    }, [optionalProducts]);

    if (!areOptionalProductsAvailable() || !optionalProducts?.length) {
        return <></>;
    }

    // TODO: Make further improvements to avoid rendering JSX in multiple places of this component
    const renderOptionalProductItems = (optionalProduct: OptionalProduct): JSX.Element => {
        const selectedProduct = selectedOptionalProducts.get(optionalProduct?.productId);
        switch (getEEProductType(optionalProduct)) {
            case ProductType.WARRANTY:
                return (
                    <OptionalProductItem
                        key={optionalProduct.productId}
                        description={getProductDescription(optionalProduct)}
                        price={optionalProduct.price}
                        imgPath={optionalProduct.imageUrl}
                        onClick={() => {
                            toggleWarrantySelection(optionalProduct);
                        }}
                        isSelected={selectedProduct?.isSelected}
                        unitOfMeasure={optionalProduct.unitOfMeasure || UNIT_MEASURE.WARRANTY}
                        infoIconContent={selectWarrantyInfoContent(optionalProduct)}
                    />
                );
            case ProductType.CUSTOM_WORK:
                return (
                    <OptionalProductWithDetailsItem
                        key={optionalProduct.productId}
                        description={getProductDescription(optionalProduct)}
                        price={optionalProduct.price}
                        detailEntries={selectedProduct?.detailEntries ?? []}
                        onDetailSave={(newDetails: SelectedItemDetailEntry[]) =>
                            updateProductDetails(optionalProduct, newDetails)
                        }
                        quantity={selectedProduct?.quantity || 0}
                        imgPath={optionalProduct.imageUrl}
                        onClick={() => {
                            onCustomWorkClick(optionalProduct);
                        }}
                        isSelected={selectedProduct?.isSelected}
                        unitOfMeasure={optionalProduct.unitOfMeasure || UNIT_MEASURE.CUSTOM_WORK}
                        showModalOnOpen
                    />
                );
            default:
                return (
                    <OptionalProductItem
                        key={optionalProduct.productId}
                        description={getProductDescription(optionalProduct)}
                        price={optionalProduct.price}
                        imgPath={optionalProduct.imageUrl}
                        onClick={() => {
                            toggleProductSelection(optionalProduct);
                        }}
                        isSelected={selectedProduct?.isSelected}
                        hasQuantitySelector
                        quantity={selectedProduct?.quantity}
                        onQuantityChange={(quantity) => setSelectedProductProps(optionalProduct, { quantity })}
                        unitOfMeasure={optionalProduct.unitOfMeasure || UNIT_MEASURE.OVERSIZE}
                    />
                );
        }
    };

    return (
        <div>
            <SectionSubtitle
                name={t('SCROLLABLE_AREA.OPTIONAL_PRODUCT.TITLE')}
                description={t('SCROLLABLE_AREA.OPTIONAL_PRODUCT.DESCRIPTION')}
                showLine
            />
            {isFetchingOptionalItems ? (
                <Spin style={{ fontSize: 24 }} />
            ) : (
                <div className="card-with-quantity card-with-quantity--large">
                    {sortedOptionalProducts.map((optionalProduct) => renderOptionalProductItems(optionalProduct))}
                </div>
            )}
        </div>
    );
};

export default OptionalProductsSection;
